import PropTypes from 'prop-types';
import { css, etiColors } from '@eti/styles';
import { useTranslation } from '@eti/providers';
import { Icon as EtiIcon } from '@eti/components';
import { CheckedBaggageSummary as CheckedBaggageSummaryType } from '@eti/types';
import { m as motion } from 'framer-motion';
import { getTravelersWithBaggageProductsOnly } from '../utils/traveler';
import { getSegmentCheckedBaggageSummaries } from '../utils/segment';
import TravelerListTravelers from './TravelerListTravelers';

const { grayScale } = etiColors;

const headerStyles = css`
  background-color: ${grayScale[4]};
  color: ${grayScale[10]};
  display: flex;
  font-size: 0.875rem;
  padding: 0.625rem;
`;

const iconStyles = css`
  margin-left: 0;
  margin-right: 0.5rem;

  & [dir='rtl'] {
    margin-left: 0.5rem;
    margin-right: 0;
  }
`;

const titleStyles = css`
  flex: 1;
  font-weight: 700;
  text-transform: uppercase;
`;

const segmentHeaderStyles = css`
  background-color: ${grayScale[2]};
  border-top: 1px solid ${grayScale[3]};
  color: ${grayScale[10]};
  display: flex;
  font-size: 0.875rem;
  font-weight: 600;
  padding: 0.4375rem 0.625rem;

  &:first-of-type {
    border-top: none;
  }
`;

const motionVariants = {
  visible: {
    height: 'auto',
    opacity: 1,
  },
  hidden: {
    height: 0,
    opacity: 0,
  },
};

const animatedTravelerListStyles = css`
  overflow-y: hidden;
`;

const TravelerList = ({ travelers = [], isExpanded, segments = [], checkedBaggageSummary }) => {
  const { t } = useTranslation();

  return (
    <>
      <div className={headerStyles}>
        <EtiIcon className={iconStyles} color={grayScale[10]} id="adult-traveler" size="1.2rem" />
        <span className={titleStyles}>{t('Receipt.TravelerDetails.Title')}</span>
      </div>
      {!isExpanded && <TravelerListTravelers travelers={travelers} />}
      <motion.div
        animate={isExpanded ? 'visible' : 'hidden'}
        className={animatedTravelerListStyles}
        data-testid="tripDetails-travelers-list"
        initial={false}
        transition={{
          type: 'tween',
          duration: 0.5,
        }}
        variants={motionVariants}
      >
        {segments.map((segment, index) => (
          <div
            data-testid={`travelerList-segment-${index}-wrapper`}
            key={`${segment.boundId}_${segment.segmentId}`}
          >
            <div className={segmentHeaderStyles}>
              {segment.origin.cityName} - {segment.destination.cityName}
            </div>
            <TravelerListTravelers
              checkedBaggageSummary={getSegmentCheckedBaggageSummaries(
                checkedBaggageSummary,
                segment,
              )}
              includedCheckedBaggage={segment.includedCheckedBaggage}
              isShowingProducts
              travelers={getTravelersWithBaggageProductsOnly(travelers)}
            />
          </div>
        ))}
      </motion.div>
    </>
  );
};

TravelerList.propTypes = {
  checkedBaggageSummary: CheckedBaggageSummaryType,
  isExpanded: PropTypes.bool,
  segments: PropTypes.arrayOf(PropTypes.object),
  travelers: PropTypes.arrayOf(
    PropTypes.shape({
      ageType: PropTypes.string,
      birthDate: PropTypes.string,
      firstName: PropTypes.string,
      gender: PropTypes.string,
      id: PropTypes.string,
      lastName: PropTypes.string,
      name: PropTypes.string,
      passportNumber: PropTypes.string,
      products: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string,
          quantity: PropTypes.number,
        }),
      ),
    }),
  ),
};

export default TravelerList;
